import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    X,
    ZoomIn,
    ZoomOut,
    Download,
    Share2,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';

/* IMAGE LIST */
const fallbackImages = Array.from({ length: 95 }, (_, i) => ({
    id: i + 2,
    url: `/images/1_${String(i + 2).padStart(2, "0")}.jpeg`,
    category: "students",
    title: `Image ${i + 2}`
}));

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [zoom, setZoom] = useState(1);

    /* LOAD IMAGES */
    useEffect(() => {
        setImages(fallbackImages);
    }, []);

    const categories = ['all'];

    const filteredImages = useMemo(() => {
        return selectedCategory === 'all'
            ? images
            : images.filter((img) => img.category === selectedCategory);
    }, [images, selectedCategory]);

    const selectedImage =
        selectedIndex !== null ? filteredImages[selectedIndex] : null;

    const closeModal = useCallback(() => {
        setSelectedIndex(null);
        setZoom(1);
    }, []);

    const nextImage = useCallback(() => {
        setSelectedIndex((prev) =>
            prev === filteredImages.length - 1 ? 0 : prev + 1
        );
        setZoom(1);
    }, [filteredImages.length]);

    const prevImage = useCallback(() => {
        setSelectedIndex((prev) =>
            prev === 0 ? filteredImages.length - 1 : prev - 1
        );
        setZoom(1);
    }, [filteredImages.length]);

    const handleDownload = useCallback(() => {
        if (!selectedImage) return;

        const link = document.createElement('a');
        link.href = selectedImage.url;
        link.download = selectedImage.title || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [selectedImage]);

    const handleShare = useCallback(async () => {
        if (!selectedImage) return;

        try {
            const shareUrl = window.location.origin + selectedImage.url;

            if (navigator.share) {
                await navigator.share({
                    title: selectedImage.title,
                    url: shareUrl,
                });
            } else {
                await navigator.clipboard.writeText(shareUrl);
                alert('Image link copied to clipboard!');
            }
        } catch (err) {
            console.error('Share failed:', err);
        }
    }, [selectedImage]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedIndex === null) return;

            if (e.key === 'ArrowRight') nextImage();
            else if (e.key === 'ArrowLeft') prevImage();
            else if (e.key === 'Escape') closeModal();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedIndex, nextImage, prevImage, closeModal]);

    return (
        <div className="min-h-screen pt-24">

            {/* HERO */}
            <section className="relative py-20 overflow-hidden bg-gradient-to-br from-violet-900 via-purple-800 to-blue-900 text-white">
                <div className="container mx-auto px-4 md:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="font-outfit font-bold text-5xl md:text-7xl mb-6 text-white">
                            Gallery
                        </h1>
                        <p className="font-dm-sans text-xl md:text-2xl max-w-3xl mx-auto text-white/95">
                            Capturing moments of excellence and joy
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* GALLERY */}
            <section className="py-24 md:py-32 bg-white">
                <div className="container mx-auto px-4 md:px-8">

                    {/* CATEGORY FILTER */}
                    <div className="flex flex-wrap justify-center gap-4 mb-12">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => {
                                    setSelectedCategory(category);
                                    setSelectedIndex(null);
                                }}
                                className={`px-6 py-3 rounded-full font-semibold capitalize transition-all transform hover:scale-105 ${selectedCategory === category
                                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg'
                                    : 'bg-white text-slate-700 hover:bg-slate-50 border-2 border-slate-200 hover:border-red-600'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* GRID (X-AXIS LOADING) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredImages.map((image, index) => (
                            <motion.div
                                key={image.id || index}
                                className="cursor-pointer"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.05 }}
                                onClick={() => {
                                    setSelectedIndex(index);
                                    setZoom(1);
                                }}
                            >
                                <div className="relative group overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-all">
                                    <img
                                        src={image.url}
                                        alt={image.title || "gallery-image"}
                                        loading="lazy"
                                        className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-110"
                                    />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LIGHTBOX MODAL */}
            {selectedImage && (
                <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">

                    <button
                        onClick={closeModal}
                        className="absolute top-6 right-6 bg-black/50 text-white p-3 rounded-full hover:bg-red-600"
                    >
                        <X size={24} />
                    </button>

                    <div className="absolute top-6 left-6 flex gap-3">
                        <button
                            onClick={() => setZoom((z) => z + 0.2)}
                            className="bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
                        >
                            <ZoomIn />
                        </button>

                        <button
                            onClick={() => setZoom((z) => (z > 0.6 ? z - 0.2 : z))}
                            className="bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
                        >
                            <ZoomOut />
                        </button>

                        <button
                            onClick={handleDownload}
                            className="bg-black/50 text-white p-3 rounded-full hover:bg-green-600"
                        >
                            <Download />
                        </button>

                        <button
                            onClick={handleShare}
                            className="bg-black/50 text-white p-3 rounded-full hover:bg-purple-600"
                        >
                            <Share2 />
                        </button>
                    </div>

                    <button
                        onClick={prevImage}
                        className="absolute left-6 bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
                    >
                        <ChevronLeft size={32} />
                    </button>

                    <button
                        onClick={nextImage}
                        className="absolute right-6 bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
                    >
                        <ChevronRight size={32} />
                    </button>

                    <img
                        src={selectedImage.url}
                        alt={selectedImage.title || "gallery-image"}
                        style={{ transform: `scale(${zoom})` }}
                        className="max-h-[85vh] max-w-[90vw] transition-transform duration-300"
                    />

                    <div className="absolute bottom-6 text-white text-sm">
                        {selectedIndex + 1} / {filteredImages.length}
                    </div>

                    <div
                        className="absolute inset-0 -z-10"
                        onClick={closeModal}
                    ></div>
                </div>
            )}
        </div>
    );
};

export default Gallery;